import { useMemo } from "react";
import { keepPreviousData, useQuery } from "@tanstack/react-query";
import { useApiConfig } from "@visioni/a3s-client-api/app";
import { expandEndpoint } from "@visioni/a3s-client-api";
import { match } from "ts-pattern";
import { endOfDay, format, startOfDay } from "date-fns";
import { AssetCameraKey, SiteId } from "@visioni/a3s-schema";
import {
  listCompilationsEndpoint,
  type CompilationItem,
  type CompilationStatus,
  type ListCompilationsResponse,
} from "../../client-messages/compilation";
import type { CompilationListItem } from "../components/CompilationList";
import type { CompilationRowStatus } from "../components/CompilationRow";
import { failureReasonLabel } from "../helpers/failure-reason-label";

// ---------------------------------------------------------------------------
// Options
// ---------------------------------------------------------------------------

export interface UseCompilationsListOptions {
  /**
   * Every filter below is applied by the database, so a page is only short when
   * the results genuinely ran out — no client-side filtering pass, and no
   * pages arriving empty because their contents were discarded here.
   */
  status?: CompilationStatus | undefined;
  siteId?: string | undefined;
  cameraKey?: string | undefined;
  dateRange?: { start: Date | null; end: Date | null } | undefined;
  /**
   * Map of camera key → display name. This hook has no camera-name source of
   * its own — the wire item only carries `subject.camera` (a raw key) — so
   * the caller supplies names it already has (e.g. from its own camera list),
   * mirroring `use-activity-list.ts`'s `cameraNames` option.
   */
  cameraNames?: ReadonlyMap<string, string> | undefined;
  /** 1-based page to read. Defaults to the first. */
  page?: number | undefined;
  /** Rows per page. Defaults to {@link DEFAULT_PAGE_SIZE}. */
  pageSize?: number | undefined;
  enabled?: boolean;
}

// ---------------------------------------------------------------------------
// Result
// ---------------------------------------------------------------------------

export interface CompilationsListResult {
  /** Rows on the requested page only. */
  readonly items: readonly CompilationListItem[];
  /** Total matching the filters, across every page. */
  readonly totalCount: number;
  /** True only on the first load of a filter/page combination never seen before. */
  readonly isLoading: boolean;
  /**
   * True while any read is in flight, including a page change served from the
   * previous page's rows and a background poll. Drives the pagination
   * control's disabled state without blanking the table.
   */
  readonly isFetching: boolean;
  readonly error: Error | null;
}

// ---------------------------------------------------------------------------
// View model mapping
// ---------------------------------------------------------------------------

/** Rows per page when the caller doesn't choose. Matches a `pageSizeOptions` entry. */
export const DEFAULT_PAGE_SIZE = 10;

/**
 * How often the list re-reads while any job is still moving.
 *
 * Compilations take minutes, not seconds — the edge downloads, transcodes and
 * concatenates every segment — so this only needs to be often enough that a row
 * does not look stuck, not to feel live.
 */
const POLL_INTERVAL_MS = 30_000;

/** Statuses a job can still move on from, and so worth re-reading for. */
const TRANSIENT_STATUSES: readonly CompilationStatus[] = ["queued", "processing", "uploading"];

/**
 * Polls only while something on the current page can still change.
 *
 * A settled page — every row ready or failed — will read identically
 * forever, so polling it just burns a Lambda invocation and a query every 30s
 * per viewer. Any newly requested job re-enters through the request mutation's
 * invalidation rather than needing the timer to have been running.
 *
 * Scoped to the page on screen, which is the whole point of paging rather than
 * accumulating: an infinite query refetches *every* page it has loaded on each
 * tick, so a deeply scrolled list cost one request per loaded page per 30s.
 * Here it is always exactly one, however deep the reader has gone.
 */
export function pollIntervalFor(page: ListCompilationsResponse | undefined): number | false {
  const anyMoving = page?.data.some((item) => TRANSIENT_STATUSES.includes(item.status)) ?? false;
  return anyMoving ? POLL_INTERVAL_MS : false;
}

/**
 * Resolves the picked date range to the absolute instants the endpoint filters
 * on.
 *
 * This has to happen here rather than server-side: the picker yields
 * start-of-day for *both* ends, and only the client knows which timezone those
 * days belong to — the Lambda runs in UTC. Sending a bare calendar date for
 * either bound would have it resolved against the wrong day, dropping late-day
 * footage west of UTC and pulling in the next local morning east of it.
 */
export function toDateBounds(dateRange?: { start: Date | null; end: Date | null }): {
  readonly dateFrom: string | undefined;
  readonly dateTo: string | undefined;
} {
  return {
    dateFrom: dateRange?.start ? startOfDay(dateRange.start).toISOString() : undefined,
    dateTo: dateRange?.end ? endOfDay(dateRange.end).toISOString() : undefined,
  };
}

/**
 * A `queued` job stuck this long with nothing else queued behind it on the
 * same camera is presumed dead rather than merely slow — legitimate delays
 * (edge reboot, IoT retry/backoff, resource contention) resolve well inside
 * this window. This only ever surfaces as a warning alongside the `queued`
 * status, never as a status of its own: the underlying record stays `queued`
 * server-side, so the warning clears itself the moment the job progresses.
 */
export const STALE_QUEUED_THRESHOLD_MS = 30 * 60 * 1000;

/**
 * A `queued` job isn't presumed dead if another job is queued behind it for
 * the same camera — that job is legitimately waiting its turn, however long
 * the wait looks from the outside.
 */
export function isStaleQueued(item: CompilationItem, now: number): boolean {
  if (item.status !== "queued") return false;
  // Counted by the endpoint, not here: filters and paging mean the siblings
  // may not be among the rows this hook holds.
  if ((item.queuedSiblings ?? 0) > 0) return false;
  return now - new Date(item.requestedAt).getTime() > STALE_QUEUED_THRESHOLD_MS;
}

/**
 * Maps the wire status to the row's display status, which is now the wire
 * status verbatim — every filterable status filters in the database, and the
 * pagination count is exact as a result.
 *
 * A `ready` job carrying no signed URL used to show as `expired`. That status
 * existed only here, so filtering by it had to happen client-side over the
 * fetched page, which made `totalCount` count every `ready` job rather than
 * the URL-less ones. Such a job shouldn't occur anyway — the endpoint signs a
 * URL whenever it reports `ready` — and when it does the row simply offers no
 * watch or download action, which those buttons already handle by their
 * absence.
 *
 * A stale `queued` job stays
 * `queued` and is flagged via {@link CompilationListItem.staleQueued}
 * instead. Relabelling it `failed` here would put the row out of step with
 * the status filter the moment that filter is served from the store rather
 * than from this mapped list: "Failed" would exclude the very rows the list
 * calls failed, and "Queued" would return rows it calls something else.
 * Staleness is a presentation concern, so it rides alongside the status
 * instead of overwriting it.
 */
export function toRowStatus(item: CompilationItem): CompilationRowStatus {
  return match(item.status)
    .with("queued", () => "queued" as const)
    .with("processing", () => "processing" as const)
    .with("uploading", () => "uploading" as const)
    .with("failed", () => "failed" as const)
    .with("ready", () => "ready" as const)
    .exhaustive();
}

/** Display format for the row's date/time fields, e.g. "17 Jun 2026, 2:00pm". */
function formatDisplayDate(isoString: string): string {
  return format(new Date(isoString), "d MMM yyyy, h:mmaaa");
}

export function toViewModel(
  item: CompilationItem,
  cameraNames?: ReadonlyMap<string, string>,
  options?: { readonly stale?: boolean },
): CompilationListItem {
  const cameraKey = AssetCameraKey.toString(item.subject.camera);
  return {
    id: item.requestId,
    siteId: SiteId.toString(item.subject.site),
    cameraKey,
    status: toRowStatus(item),
    staleQueued: options?.stale ?? false,
    failureReason: failureReasonLabel(item.failureReason),
    cameraLabel: cameraNames?.get(cameraKey) ?? cameraKey,
    requestedAt: formatDisplayDate(item.requestedAt),
    rangeStart: formatDisplayDate(item.startTime),
    rangeEnd: formatDisplayDate(item.endTime),
    videoUrl: item.video?.url,
    // Left undefined (rather than falling back to `url`) for a Lambda not yet
    // redeployed with the download-signing change — the download button
    // disables itself when this is absent (see `CompilationRow`).
    downloadUrl: item.video?.downloadUrl,
  };
}

// ---------------------------------------------------------------------------
// Hook
// ---------------------------------------------------------------------------

/**
 * Paginated list of compilation jobs for the current tenant.
 *
 * Reads one page at a time rather than accumulating them. The rows live in
 * Postgres behind an exact `totalCount`, so a page is directly addressable —
 * and discrete pages avoid two problems the accumulating form has here: the
 * poll refetching every loaded page on each tick, and an offset boundary that
 * shifts under `created_at DESC` whenever a new compilation is requested while
 * the list is open, which is precisely what this screen is used to do.
 */
export function useCompilationsList(options: UseCompilationsListOptions = {}): CompilationsListResult {
  const apiConfig = useApiConfig();
  const {
    status,
    siteId,
    cameraKey,
    dateRange,
    cameraNames,
    page = 1,
    pageSize = DEFAULT_PAGE_SIZE,
    enabled = true,
  } = options;
  const { tenant, authenticatedFetch } = apiConfig;

  const { dateFrom, dateTo } = toDateBounds(dateRange);
  const offset = (page - 1) * pageSize;

  const query = useQuery<ListCompilationsResponse>({
    queryKey: [
      "compilations",
      "list",
      tenant?.tenantId,
      status,
      siteId,
      cameraKey,
      dateFrom,
      dateTo,
      page,
      pageSize,
    ] as const,
    enabled: enabled && !!tenant,
    // Page and page-size changes keep showing the previous page's rows until
    // the new ones arrive, so the table doesn't collapse to a skeleton and
    // back on every click.
    placeholderData: keepPreviousData,
    queryFn: async ({ signal }) => {
      if (!tenant) throw new Error("Tenant not ready");

      const url =
        tenant.apiBaseUrl +
        expandEndpoint(listCompilationsEndpoint, {
          path: { tenantId: tenant.tenantId },
          query: {
            status,
            siteId,
            cameraKey,
            dateFrom,
            dateTo,
            limit: String(pageSize),
            offset: String(offset),
          },
        });

      const response = await authenticatedFetch(url, { signal });
      if (!response.ok) {
        let message = `Request failed: ${String(response.status)}`;
        try {
          const text = await response.text();
          if (text) {
            try {
              const json = JSON.parse(text) as { message?: string };
              message = json.message ?? text;
            } catch {
              message = text;
            }
          }
        } catch {
          // fall through with default message
        }
        throw new Error(message);
      }

      const data: unknown = await response.json();
      return listCompilationsEndpoint.responseSchema.parse(data);
    },
    // One request per tick regardless of how deep the reader has paged, and
    // none at all once the page on screen has settled.
    refetchInterval: (query) => pollIntervalFor(query.state.data),
    refetchOnMount: "always",
    refetchOnWindowFocus: true,
    staleTime: 0,
  });

  const items = useMemo<readonly CompilationListItem[]>(() => {
    const now = Date.now();
    return (query.data?.data ?? []).map((item) => toViewModel(item, cameraNames, { stale: isStaleQueued(item, now) }));
  }, [query.data, cameraNames]);

  return {
    items,
    totalCount: query.data?.totalCount ?? 0,
    isLoading: query.isLoading,
    isFetching: query.isFetching,
    error: query.error instanceof Error ? query.error : null,
  };
}
